import React, { useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "./../backendconfig";
import "./MediaManager.css";

const backend = BACKEND_URL.replace(/\/+$/, "");
const api = `${backend}/media-manager-api.php`;

const sections = [
  ["gallery", "Gallery"],
  ["banner", "Banner"],
  ["about", "About"],
  ["free-demo", "Free Demo"],
];

async function readResponse(response) {
  let data;

  try {
    data = await response.json();
  } catch {
    throw new Error(
      "Invalid API response. Check the PHP file and backend URL.",
    );
  }

  if (!response.ok || !data.ok) {
    throw new Error(
      data.message || data.errors?.join("\n") || "Unable to process request.",
    );
  }

  return data;
}

function getImageUrl(path) {
  try {
    const url = new URL(path, `${backend}/`);
    return ["http:", "https:"].includes(url.protocol) ? url.href : "";
  } catch {
    return "";
  }
}

function MediaImage({ src, alt, className = "" }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  return failed ? (
    <div className="mm-image-error" role="img" aria-label={alt}>
      Image unavailable
    </div>
  ) : (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

export default function MediaManager() {
  const [type, setType] = useState("gallery");
  const [images, setImages] = useState([]);
  const [csrf, setCsrf] = useState("");
  const [files, setFiles] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [errors, setErrors] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(null);

  const fileInput = useRef(null);
  const dialog = useRef(null);
  const requestBusy = useRef(false);
  const opener = useRef(null);

  const single = ["about", "free-demo"].includes(type);
  const sectionLabel = sections.find(([key]) => key === type)?.[1];

  const totalPages = Math.max(1, Math.ceil(images.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const offset = (currentPage - 1) * perPage;
  const pageImages = images.slice(offset, offset + perPage);
  const photo = photoIndex === null ? null : images[photoIndex];

  async function fetchImages(section, signal) {
    const response = await fetch(`${api}?type=${encodeURIComponent(section)}`, {
      credentials: "include",
      cache: "no-store",
      signal,
    });

    return readResponse(response);
  }

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setCsrf("");
    setImages([]);
    setFiles([]);
    setPage(1);
    setMessages([]);
    setErrors([]);

    if (fileInput.current) {
      fileInput.current.value = "";
    }

    fetchImages(type, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          setImages(data.images);
          setCsrf(data.csrf);
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          setErrors([error.message]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [type]);

  const viewerOpen = photoIndex !== null;

  useEffect(() => {
    if (!viewerOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    if (!dialog.current?.open) {
      dialog.current?.showModal();
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [viewerOpen]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  function closeViewer() {
    dialog.current?.close();
    setPhotoIndex(null);
  }

  function movePhoto(direction) {
    if (!images.length) return;

    setPhotoIndex((current) =>
      current === null
        ? null
        : (current + direction + images.length) % images.length,
    );
  }

  async function send(action, filename = "") {
    if (requestBusy.current || loading || !csrf) return;

    if (
      action === "delete" &&
      !window.confirm(`Permanently delete "${filename}"?`)
    ) {
      return;
    }

    if (action === "upload") {
      if (!files.length) {
        setErrors(["Choose images first."]);
        return;
      }

      if (files.length > (single ? 1 : 20)) {
        setErrors([
          single ? "Choose only one image." : "Choose up to 20 images.",
        ]);
        return;
      }

      if (files.some((file) => file.size > 10 * 1024 * 1024)) {
        setErrors(["Maximum image size is 10 MB per image."]);
        return;
      }
    }

    requestBusy.current = true;
    setBusy(true);
    setMessages([]);
    setErrors([]);

    try {
      const body = new FormData();

      body.append("action", action);
      body.append("type", type);
      body.append("csrf", csrf);

      if (action === "delete") {
        body.append("filename", filename);
      } else {
        files.forEach((file) => body.append("images[]", file));
      }

      const response = await fetch(api, {
        method: "POST",
        credentials: "include",
        body,
      });

      const data = await readResponse(response);

      setMessages(data.messages || []);
      setErrors(data.errors || []);

      if (action === "upload") {
        setFiles([]);
        setPage(1);

        if (fileInput.current) {
          fileInput.current.value = "";
        }
      }

      const refreshed = await fetchImages(type);

      setImages(refreshed.images);
      setCsrf(refreshed.csrf);
    } catch (error) {
      setErrors([error.message]);
    } finally {
      requestBusy.current = false;
      setBusy(false);
    }
  }

  const pageNumbers = [];

  for (let number = 1; number <= totalPages; number++) {
    if (
      number === 1 ||
      number === totalPages ||
      Math.abs(number - currentPage) <= 2
    ) {
      if (
        pageNumbers.length &&
        number - pageNumbers[pageNumbers.length - 1] > 1
      ) {
        pageNumbers.push(number - 0.5);
      }

      pageNumbers.push(number);
    }
  }

  return (
    <main className="mm-page">
      <header className="mm-header">
        <div>
          <h1>Image Manager</h1>
          <p>Upload and manage images for your website sections.</p>
        </div>

        <span className="mm-count">{images.length} images</span>
      </header>

      <div className="mm-limit-toolbar">
        <label htmlFor="mm-images-per-page">
          Images per page:
          <select
            id="mm-images-per-page"
            value={perPage}
            onChange={(event) => {
              setPerPage(Number(event.target.value));
              setPage(1);
            }}
          >
            <option value={12}>12</option>
            <option value={20}>20</option>
            <option value={24}>24</option>
            <option value={36}>36</option>
            <option value={48}>48</option>
          </select>
        </label>

        {!loading && (
          <span className="mm-limit-results" role="status">
            {images.length > 0
              ? `Showing ${offset + 1}–${Math.min(
                  offset + perPage,
                  images.length,
                )} of ${images.length} images`
              : "0 images"}
          </span>
        )}
      </div>

      <div className="mm-tabs" role="group" aria-label="Image section">
        {sections.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={type === key ? "mm-active" : ""}
            aria-pressed={type === key}
            disabled={busy}
            onClick={() => setType(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <section className="mm-panel">
        <h2>{sectionLabel} Images</h2>

        <p className="mm-hint">
          {single ? "One image per upload." : "Up to 20 images per upload."}{" "}
          Maximum 10 MB per image.
        </p>

        {messages.map((message, index) => (
          <div className="mm-notice mm-success" role="status" key={index}>
            {message}
          </div>
        ))}

        {errors.map((error, index) => (
          <div className="mm-notice mm-error" role="alert" key={index}>
            {error}
          </div>
        ))}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            send("upload");
          }}
        >
          <input
            ref={fileInput}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,.gif,.bmp,.avif"
            multiple={!single}
            required
            aria-label="Choose images"
            disabled={busy || loading || !csrf}
            onChange={(event) => setFiles(Array.from(event.target.files || []))}
          />

          {files.length > 0 && (
            <p className="mm-hint">{files.length} file(s) selected</p>
          )}

          <button
            type="submit"
            className="mm-primary"
            disabled={busy || loading || !csrf}
          >
            <i className="bi bi-cloud-arrow-up" aria-hidden="true" />
            {busy ? "Processing…" : single ? "Upload Image" : "Upload Images"}
          </button>
        </form>
      </section>

      <div className="mm-list-heading">
        <h2>Uploaded Images</h2>
        <span>Newest first</span>
      </div>

      {loading ? (
        <p role="status">Loading images…</p>
      ) : (
        <>
          <div className="mm-grid">
            {pageImages.map((image, index) => (
              <article className="mm-card" key={image.name}>
                <button
                  type="button"
                  className="mm-open"
                  aria-label={`View ${image.name}`}
                  onClick={(event) => {
                    opener.current = event.currentTarget;
                    setPhotoIndex(offset + index);
                  }}
                >
                  <MediaImage src={getImageUrl(image.url)} alt={image.name} />
                </button>

                <button
                  type="button"
                  className="mm-delete"
                  title="Delete image"
                  aria-label={`Delete ${image.name}`}
                  disabled={busy}
                  onClick={() => send("delete", image.name)}
                >
                  <i className="bi bi-trash" aria-hidden="true" />
                </button>
              </article>
            ))}
          </div>

          {images.length === 0 && <p>No images in this folder yet.</p>}

          {images.length > 0 && (
            <div className="mm-pagination-wrap">
              <p>
                Showing {offset + 1}–{Math.min(offset + perPage, images.length)}{" "}
                of {images.length} images
              </p>

              {totalPages > 1 && (
                <nav className="mm-pagination" aria-label="Image pages">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                  >
                    ‹ Previous
                  </button>

                  {pageNumbers.map((number) =>
                    Number.isInteger(number) ? (
                      <button
                        key={number}
                        type="button"
                        className={number === currentPage ? "mm-active" : ""}
                        aria-current={
                          number === currentPage ? "page" : undefined
                        }
                        aria-label={`Page ${number}`}
                        onClick={() => setPage(number)}
                      >
                        {number}
                      </button>
                    ) : (
                      <span key={number}>…</span>
                    ),
                  )}

                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setPage(Math.min(totalPages, currentPage + 1))
                    }
                  >
                    Next ›
                  </button>
                </nav>
              )}
            </div>
          )}
        </>
      )}

      <dialog
        ref={dialog}
        className="mm-viewer"
        aria-labelledby="mm-viewer-title"
        onClose={() => {
          setPhotoIndex(null);
          opener.current?.focus();
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.preventDefault();
            movePhoto(event.key === "ArrowRight" ? 1 : -1);
          }
        }}
        onClick={(event) => {
          if (event.target !== event.currentTarget) return;

          const rect = event.currentTarget.getBoundingClientRect();

          if (
            event.clientX < rect.left ||
            event.clientX > rect.right ||
            event.clientY < rect.top ||
            event.clientY > rect.bottom
          ) {
            closeViewer();
          }
        }}
      >
        <div className="mm-viewer-header">
          <h2 id="mm-viewer-title">{photo?.name || "Image Preview"}</h2>

          <button type="button" aria-label="Close viewer" onClick={closeViewer}>
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        {photo && (
          <div className="mm-stage">
            <MediaImage
              src={getImageUrl(photo.url)}
              alt={photo.name}
              className="mm-full-image"
            />

            <button
              type="button"
              className="mm-arrow mm-prev"
              aria-label="Previous image"
              disabled={images.length < 2}
              onClick={() => movePhoto(-1)}
            >
              <i className="bi bi-chevron-left" aria-hidden="true" />
            </button>

            <button
              type="button"
              className="mm-arrow mm-next"
              aria-label="Next image"
              disabled={images.length < 2}
              onClick={() => movePhoto(1)}
            >
              <i className="bi bi-chevron-right" aria-hidden="true" />
            </button>
          </div>
        )}

        <div className="mm-viewer-footer">
          <span aria-live="polite">
            {photoIndex === null ? 0 : photoIndex + 1} / {images.length}
          </span>
        </div>
      </dialog>
    </main>
  );
}
